// The Daily reminder: one local notification a day, at the hour the player
// chose, only while that day's Daily is still unplayed.
//
// Why (2026-10-13). Next-day return sat at 11-12% through every change to the
// first games: the first session got much better and nothing brought anyone
// back the next day. The Daily is the reason to come back, and nothing told
// anyone it was there.
//
// Shape, all of it decided here so the screens stay dumb:
// - Offered once, on the result screen of the first finished game, Android
//   only. Profile turns it on or off at any time.
// - The hour is the one the player said yes at, kept between 09:00 and 21:00.
// - Only the next two reminders exist at any time, rebuilt whenever the app
//   opens or a Daily ends (lib/reminderPlan.ts). A played day gets none; a
//   player who stops opening the app hears twice more, then silence.
// - Inexact alarms. A few minutes of drift do not matter, and the plugin's
//   exact path sends Android 12+ players to a system settings screen.
// - A tap opens the app straight into that day's Daily (App.tsx).

import { LocalNotifications } from '@capacitor/local-notifications';
import { isNative } from '../utils/platform';
import { logAppEvent } from './appEvents';
import { clampHour, planReminders, REMINDER_IDS, type DailyRecord } from './reminderPlan';

const PREF_KEY = 'wot_reminder_v1';
const CHANNEL_ID = 'daily';

export type ReminderState = 'on' | 'off' | 'denied';
export interface ReminderPref {
  state: ReminderState;
  hour: number;
}
type Source = 'results' | 'profile';

export function readReminderPref(): ReminderPref | null {
  try {
    const p = JSON.parse(localStorage.getItem(PREF_KEY) || 'null');
    return p && (p.state === 'on' || p.state === 'off' || p.state === 'denied') && Number.isFinite(p.hour) ? p : null;
  } catch {
    return null;
  }
}

function writeReminderPref(pref: ReminderPref): void {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(pref)); } catch { /* storage blocked */ }
}

function dailyRecord(): DailyRecord | null {
  try { return JSON.parse(localStorage.getItem('wot_daily_v1') || 'null'); } catch { return null; }
}

// Dev only: ?remind=1 shows the result-screen offer in a browser, where there
// is no notification plugin, so the card can be looked at. Read at module
// load because the router rewrites the URL before any screen mounts.
const DEV_KEY = 'wot_remind_dev';
try {
  if (import.meta.env.DEV && typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('remind') === '1') {
    sessionStorage.setItem(DEV_KEY, '1');
  }
} catch { /* storage blocked */ }
function devPreview(): boolean {
  try { return import.meta.env.DEV && sessionStorage.getItem(DEV_KEY) === '1'; } catch { return false; }
}

/** Whether this device can carry the reminder at all (Android, or the dev preview). */
export function reminderSupported(): boolean {
  return isNative || devPreview();
}

/** The result screen's one-time offer: never answered on this install yet. */
export function shouldOfferReminder(): boolean {
  return reminderSupported() && readReminderPref() === null;
}

async function cancelPending(): Promise<void> {
  await LocalNotifications.cancel({ notifications: REMINDER_IDS.map((id) => ({ id })) });
}

/**
 * Rebuilds the next two reminders from now. Called on every app open and
 * after every Daily; a no-op unless the player turned the reminder on.
 */
export async function refreshReminders(): Promise<void> {
  if (!isNative) return;
  const pref = readReminderPref();
  if (pref?.state !== 'on') return;
  try {
    // Notifications switched off in Android settings since: say so in
    // Profile rather than scheduling into the void.
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      writeReminderPref({ ...pref, state: 'denied' });
      return;
    }
    await cancelPending();
    const plan = planReminders(new Date(), pref.hour, dailyRecord());
    if (plan.length === 0) return;
    await LocalNotifications.schedule({
      notifications: plan.map((p) => ({
        id: p.id,
        title: p.title,
        body: p.body,
        schedule: { at: p.at, allowWhileIdle: true },
        channelId: CHANNEL_ID,
        isExactNotification: false,
        extra: { kind: 'daily' },
      })),
    });
  } catch { /* no plugin, no permission: the game goes on */ }
}

/**
 * Turns the reminder on: Android's permission prompt, a channel the player
 * can find in system settings, then the first two reminders. The hour is the
 * current one, kept inside the day.
 */
export async function enableReminder(source: Source): Promise<ReminderState> {
  const hour = clampHour(new Date().getHours());
  if (!isNative) {
    if (devPreview()) writeReminderPref({ state: 'on', hour });
    return devPreview() ? 'on' : 'off';
  }
  try {
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') perm = await LocalNotifications.requestPermissions();
    if (perm.display !== 'granted') {
      writeReminderPref({ state: 'denied', hour });
      logAppEvent('reminder_denied', { source });
      return 'denied';
    }
    await LocalNotifications.createChannel({
      id: CHANNEL_ID,
      name: 'Daily reminder',
      description: "A nudge when today's Daily is ready",
      importance: 3,
      visibility: 1,
    });
    writeReminderPref({ state: 'on', hour });
    logAppEvent('reminder_on', { source, hour });
    await refreshReminders();
    return 'on';
  } catch {
    return readReminderPref()?.state ?? 'off';
  }
}

/** Turns it off, or records "not now" on the offer; either way nothing fires. */
export async function disableReminder(source: Source): Promise<void> {
  writeReminderPref({ state: 'off', hour: readReminderPref()?.hour ?? clampHour(new Date().getHours()) });
  logAppEvent('reminder_off', { source });
  if (!isNative) return;
  try { await cancelPending(); } catch { /* nothing scheduled */ }
}

/** The hour as the player sees it, for the confirmation line. */
export function reminderHourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * A tap on a reminder: `open` takes the player into the Daily. Cold starts
 * work too, because the plugin holds the tap until a listener takes it.
 */
export function listenForReminderTaps(open: () => void): () => void {
  if (!isNative) return () => {};
  const handle = LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
    if (action.notification?.extra?.kind !== 'daily') return;
    logAppEvent('reminder_open');
    open();
  });
  return () => { void handle.then((h) => h.remove()); };
}
